import { getContinentByCountry } from '@/utils/countryToContinent'
import { getPrimaryCountryCode } from '@/utils/countryRegionDisplay'
import logger from '@/utils/logger'

const USER_COUNTRY_LOG_PREFIX = '[UserCountry]'

export function logUserCountry(label, payload) {
  if (payload === undefined) {
    logger.debug(USER_COUNTRY_LOG_PREFIX, label)
    return
  }
  logger.debug(USER_COUNTRY_LOG_PREFIX, label, payload)
}

const TIMEZONE_TO_COUNTRY = {
  'Africa/Cairo': 'EG',
  'Africa/Johannesburg': 'ZA',
  'Africa/Lagos': 'NG',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Mexico_City': 'MX',
  'America/New_York': 'US',
  'America/Sao_Paulo': 'BR',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'Asia/Bangkok': 'TH',
  'Asia/Dubai': 'AE',
  'Asia/Hong_Kong': 'HK',
  'Asia/Jakarta': 'ID',
  'Asia/Kolkata': 'IN',
  'Asia/Kuala_Lumpur': 'MY',
  'Asia/Manila': 'PH',
  'Asia/Seoul': 'KR',
  'Asia/Shanghai': 'CN',
  'Asia/Singapore': 'SG',
  'Asia/Taipei': 'TW',
  'Asia/Tokyo': 'JP',
  'Australia/Melbourne': 'AU',
  'Australia/Sydney': 'AU',
  'Europe/Amsterdam': 'NL',
  'Europe/Berlin': 'DE',
  'Europe/Istanbul': 'TR',
  'Europe/London': 'GB',
  'Europe/Madrid': 'ES',
  'Europe/Moscow': 'RU',
  'Europe/Paris': 'FR',
  'Europe/Rome': 'IT',
  'Europe/Stockholm': 'SE',
  'Europe/Vienna': 'AT',
  'Europe/Warsaw': 'PL',
  'Europe/Zurich': 'CH',
  'Pacific/Auckland': 'NZ',
}

function getRegionFromLanguageTag(languageTag) {
  const language = String(languageTag || '').trim()
  if (!language) return null

  try {
    const locale = new Intl.Locale(language)
    if (locale.region) {
      return locale.region.toUpperCase()
    }
  } catch (_) {
    /* ignore */
  }

  const match = language.match(/[-_]([A-Za-z]{2})\b/)
  return match ? match[1].toUpperCase() : null
}

function getRegionFromNavigatorLanguage() {
  return getRegionFromLanguageTag(navigator.language)
}

function getRegionFromTelegramUser(tg = window.Telegram?.WebApp) {
  return getRegionFromLanguageTag(tg?.initDataUnsafe?.user?.language_code)
}

function getRegionFromTimeZone() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!timeZone) return null
    return TIMEZONE_TO_COUNTRY[timeZone] || null
  } catch (_) {
    return null
  }
}

/**
 * Best-effort ISO region hint: Telegram language → timezone → browser locale.
 */
export function getUserCountryHint(tg = window.Telegram?.WebApp) {
  return getRegionFromTelegramUser(tg)
    || getRegionFromTimeZone()
    || getRegionFromNavigatorLanguage()
}

/**
 * Breakdown of each detection source — dev-only debugging.
 */
export function getUserCountryHintDebug(tg = window.Telegram?.WebApp) {
  const telegramRegion = getRegionFromTelegramUser(tg)
  const timezoneRegion = getRegionFromTimeZone()
  const navigatorRegion = getRegionFromNavigatorLanguage()
  let timezone = null
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch (_) {
    timezone = null
  }
  const hint = telegramRegion || timezoneRegion || navigatorRegion

  return {
    hint,
    winner: telegramRegion
      ? 'telegram'
      : timezoneRegion
        ? 'timezone'
        : navigatorRegion
          ? 'navigator'
          : 'none',
    sources: {
      telegram: {
        language_code: tg?.initDataUnsafe?.user?.language_code ?? null,
        region: telegramRegion,
      },
      timezone: {
        value: timezone,
        region: timezoneRegion,
      },
      navigator: {
        language: typeof navigator !== 'undefined' ? navigator.language : null,
        region: navigatorRegion,
      },
    },
  }
}

/**
 * Resolve the matched product-list country key for a tab-specific list.
 */
export function resolveUserCountryKey(hint, productList) {
  if (!hint || String(hint).trim() === '') return null
  return findMatchingProductCountry(hint, productList)
}

/**
 * Match a product-list country key for the detected region hint.
 */
export function findMatchingProductCountry(hint, productList) {
  if (!hint || !productList || typeof productList !== 'object') return null

  const keys = Object.keys(productList)
  if (!keys.length) return null

  const upperHint = String(hint).trim().toUpperCase()
  const exact = keys.find((key) => String(key).trim().toUpperCase() === upperHint)
  if (exact) return exact

  const byPrimary = keys.find((key) => getPrimaryCountryCode(key) === upperHint)
  return byPrimary || null
}

/**
 * Put the matched country first inside its continent and move that continent to the top.
 */
export function prioritizeUserCountryInGrouped(grouped, countryKey) {
  if (!countryKey || !grouped || typeof grouped !== 'object') {
    logUserCountry('prioritize: skipped', {
      countryKey: countryKey || null,
      reason: !countryKey ? 'no-country-key' : 'invalid-grouped',
    })
    return grouped
  }

  const continent = getContinentByCountry(countryKey)
  const continentData = grouped[continent]
  if (!continentData?.[countryKey]) {
    logUserCountry('prioritize: skipped', {
      countryKey,
      continent,
      reason: 'country-not-in-product-list',
      availableContinents: Object.keys(grouped),
    })
    return grouped
  }

  logUserCountry('prioritize: applied', {
    countryKey,
    continent,
    countryCountInContinent: Object.keys(continentData).length,
  })

  const sortedContinent = {
    [countryKey]: continentData[countryKey],
    ...Object.fromEntries(
      Object.entries(continentData).filter(([key]) => key !== countryKey),
    ),
  }

  return {
    [continent]: sortedContinent,
    ...Object.fromEntries(
      Object.entries(grouped).filter(([key]) => key !== continent),
    ),
  }
}

/**
 * Continent display order with the user's continent moved to the top when known.
 */
export function getOrderedContinents(baseOrder, grouped, countryKey) {
  const available = baseOrder.filter(
    (continent) => grouped[continent] && Object.keys(grouped[continent]).length > 0,
  )

  if (!countryKey) {
    logUserCountry('continent-order: default', { order: available })
    return available
  }

  const userContinent = getContinentByCountry(countryKey)
  if (!available.includes(userContinent)) {
    logUserCountry('continent-order: default', {
      countryKey,
      userContinent,
      reason: 'user-continent-not-in-list',
      order: available,
    })
    return available
  }

  const order = [userContinent, ...available.filter((continent) => continent !== userContinent)]
  logUserCountry('continent-order: user-first', {
    countryKey,
    userContinent,
    order,
  })
  return order
}
